/** /partners/:id — one integration partner: identity, contract (the gate), contacts,
 *  its sandbox, its developer keys and the claim links that mint them (G2), its activity
 *  in the external access log, and the lifecycle (suspend / resume / terminate). Writes
 *  need console_partners_manage; everything is audited server-side. */
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link as RouterLink, useParams } from 'react-router-dom';

import * as partnersApi from '@/api/partners';
import type { LifecycleStep, PartnerContact, PartnerDetail } from '@/api/partners';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { PageHeader } from '@/components/PageHeader';
import { useSnackbar } from '@/components/Snackbar';
import { usePermissions } from '@/hooks/usePermissions';
import { EmbeddedExternalAccess } from '@/features/external-access/EmbeddedExternalAccess';
import { ExternalAccessSummary } from '@/features/external-access/ExternalAccessSummary';
import { formatRelativeTime } from '@/utils/format';

import { PartnerClaimLinksSection } from './PartnerClaimLinksSection';
import { PartnerContactDialog } from './PartnerContactDialog';
import { PartnerDeveloperKeysSection } from './PartnerDeveloperKeysSection';
import { Section } from './PartnerSection';
import { PartnerContractForm, PartnerIdentityForm } from './PartnerForms';
import { PartnerLifecycleDialog } from './PartnerLifecycleDialog';
import { PartnerContractChip, PartnerStatusChip } from './partnerChips';
import { partnerErrorMessage } from './partnerErrors';

export function PartnerDetailPage() {
  const params = useParams<{ id: string }>();
  const id = Number(params.id);
  const { hasPerm } = usePermissions();
  const canManage = hasPerm('console_partners_manage');
  const [step, setStep] = useState<LifecycleStep | null>(null);

  const query = useQuery({
    queryKey: ['partners', 'detail', id],
    queryFn: () => partnersApi.retrieve(id),
    enabled: Number.isFinite(id),
  });

  if (!Number.isFinite(id)) return <Alert severity="warning">Invalid partner id.</Alert>;
  if (query.isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }
  if (query.error || !query.data) return <Alert severity="error">Couldn’t load the partner.</Alert>;
  const partner = query.data;
  const terminated = partner.status === 'terminated';

  return (
    <Box>
      <PageHeader
        helpStory="partner-onboarding"
        title={partner.name}
        titleText={partner.name}
        subtitle={`Partner ${partner.code} · ${partnersApi.PARTNER_KIND_LABELS[partner.kind]}`}
        titleAdornment={<PartnerStatusChip status={partner.status} />}
        actions={
          canManage && (
            <Box sx={{ display: 'flex', gap: 1 }}>
              {partner.status === 'active' && (
                <Button color="error" variant="outlined" onClick={() => setStep('suspend')}>
                  Suspend
                </Button>
              )}
              {partner.status === 'suspended' && (
                <Button variant="contained" onClick={() => setStep('resume')}>
                  Resume
                </Button>
              )}
              {!terminated && (
                <Button color="error" onClick={() => setStep('terminate')}>
                  Terminate
                </Button>
              )}
            </Box>
          )
        }
      />

      {partner.status !== 'active' && (
        <Alert severity={terminated ? 'error' : 'warning'} sx={{ mb: 2 }}>
          {terminated
            ? 'Terminated: every developer key of this partner is revoked.'
            : 'Suspended: the partner’s developer keys are denied and its sandbox is suspended.'}
          {partner.status_reason ? ` Reason: ${partner.status_reason}` : ''}
        </Alert>
      )}

      <Stack spacing={2} sx={{ maxWidth: 1100, pb: 4 }}>
        <Section title="Gate" actions={<PartnerContractChip partner={partner} />}>
          <Typography variant="body2" color="text.secondary">
            A developer key needs an active partner whose contract is recorded and in force. A key is minted when a
            technical contact confirms a claim link (below).
          </Typography>
        </Section>

        <SandboxSection partner={partner} canManage={canManage && partner.status === 'active'} />

        <PartnerDeveloperKeysSection partner={partner} />

        <PartnerClaimLinksSection partner={partner} canManage={canManage && !terminated} />

        {hasPerm('console_external_access_view') && (
          <Section title="Activity">
            <Stack spacing={2}>
              <ExternalAccessSummary partnerId={partner.id} />
              <EmbeddedExternalAccess partnerId={partner.id} />
            </Stack>
          </Section>
        )}

        <Section title="Contract">
          <PartnerContractForm partner={partner} canManage={canManage} />
        </Section>

        <ContactsSection partner={partner} canManage={canManage} />

        <Section title="Partner">
          <PartnerIdentityForm partner={partner} canManage={canManage} />
        </Section>
      </Stack>

      <PartnerLifecycleDialog partnerId={partner.id} partnerName={partner.name} step={step} onClose={() => setStep(null)} />
    </Box>
  );
}

function SandboxSection({ partner, canManage }: { partner: PartnerDetail; canManage: boolean }) {
  const qc = useQueryClient();
  const snackbar = useSnackbar();
  const create = useMutation({
    mutationFn: () => partnersApi.createSandbox(partner.id),
    onSuccess: () => {
      snackbar.show('Sandbox created.', 'success');
      void qc.invalidateQueries({ queryKey: ['partners'] });
    },
    onError: (e) => snackbar.show(partnerErrorMessage(e, 'The sandbox could not be created.'), 'error'),
  });
  const box = partner.sandbox;
  return (
    <Section title="Sandbox">
      {box ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography variant="body2">
              <RouterLink to={`/tour-operators/${box.id}`} style={{ color: '#0a1a3f', fontWeight: 600 }}>
                {box.code}
              </RouterLink>{' '}
              — {box.name}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Keys: {partner.keys.active} active of {partner.keys.total} · last seen{' '}
              {partner.last_seen ? formatRelativeTime(partner.last_seen) : 'never'}
            </Typography>
          </Box>
          <Button component={RouterLink} to={`/tour-operators/${box.id}/setup/partner`} variant="outlined">
            Open setup
          </Button>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ flexGrow: 1 }}>
            No sandbox yet. It is created as {partner.code}sbx, with no default key.
          </Typography>
          {canManage && (
            <Button variant="contained" disabled={create.isPending} onClick={() => create.mutate()}>
              Create sandbox
            </Button>
          )}
        </Box>
      )}
    </Section>
  );
}

function ContactsSection({ partner, canManage }: { partner: PartnerDetail; canManage: boolean }) {
  const qc = useQueryClient();
  const snackbar = useSnackbar();
  const [editing, setEditing] = useState<PartnerContact | null | undefined>(undefined);
  const [removing, setRemoving] = useState<PartnerContact | null>(null);
  const remove = useMutation({
    mutationFn: (c: PartnerContact) => partnersApi.removeContact(partner.id, c.id),
    onSuccess: () => {
      snackbar.show('Contact removed.', 'success');
      void qc.invalidateQueries({ queryKey: ['partners'] });
      setRemoving(null);
    },
    onError: (e) => snackbar.show(partnerErrorMessage(e, 'Remove failed.'), 'error'),
  });
  const hasTechnical = partner.contacts.some((c) => c.role === 'technical' && c.is_active);

  return (
    <Section
      title="Contacts"
      actions={
        canManage && (
          <Button size="small" startIcon={<AddIcon />} onClick={() => setEditing(null)}>
            Add contact
          </Button>
        )
      }
    >
      {!hasTechnical && (
        <Alert severity="info" sx={{ mb: 1.5 }}>
          No active technical contact: the hand-over (claim link) needs one.
        </Alert>
      )}
      {partner.contacts.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No contacts yet.
        </Typography>
      ) : (
        <Table size="small" aria-label="Partner contacts">
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>E-mail</TableCell>
              <TableCell>Phone</TableCell>
              <TableCell>Role</TableCell>
              <TableCell>Status</TableCell>
              {canManage && <TableCell align="right">Actions</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {partner.contacts.map((c) => (
              <TableRow key={c.id}>
                <TableCell>{c.name}</TableCell>
                <TableCell>{c.email}</TableCell>
                <TableCell>{c.phone || '—'}</TableCell>
                <TableCell>{c.role === 'technical' ? 'Technical' : 'Business'}</TableCell>
                <TableCell>
                  <Chip size="small" variant="outlined" label={c.is_active ? 'Active' : 'Inactive'} />
                </TableCell>
                {canManage && (
                  <TableCell align="right">
                    <Tooltip title="Edit">
                      <IconButton size="small" aria-label={`Edit ${c.name}`} onClick={() => setEditing(c)}>
                        <EditOutlinedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Remove">
                      <IconButton size="small" aria-label={`Remove ${c.name}`} onClick={() => setRemoving(c)}>
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      <PartnerContactDialog
        open={editing !== undefined}
        partnerId={partner.id}
        contact={editing ?? null}
        onClose={() => setEditing(undefined)}
      />
      <ConfirmDialog
        open={removing !== null}
        title="Remove contact"
        message={removing ? `Remove ${removing.name} <${removing.email}> from ${partner.name}?` : ''}
        confirmLabel="Remove"
        destructive
        onConfirm={() => removing && remove.mutate(removing)}
        onCancel={() => setRemoving(null)}
      />
    </Section>
  );
}
